import React from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { formattedDate } from "src/helpers/DateHelpers";

import { Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import CopyValue from "./_CopyValue";
import { truncateMiddle } from "./_serverHelpers";
import { rentSummary } from "./_RentForm";

// crafty-servers echoes powerMode / autoStopMinutes in camelCase; rows
// provisioned before power modes existed carry neither, and then the
// provider default (afk) is what runs.
function powerLabel(config: { [key: string]: any } | undefined) {
  if (config?.powerMode === 'always_on') return 'Always on';

  const minutes = config?.autoStopMinutes;

  return typeof minutes === 'number'
    ? `Sleeps after ${minutes} min idle`
    : 'Sleeps when idle';
}

// Identity & metadata for a server — no operational controls. Lives in the
// right sidebar on desktop (where it is visible on every tab) and is
// re-rendered inline at the top of the page under 768px, where the fixed
// sidebar is hidden.
const _ServerMeta = ({ server }: { server: ServerInterface | undefined }) => {
  const hosted = server?.hosted_server;

  const Kind = ({ kind }: { kind: string | undefined }) => {
    if (!kind) return null;

    return <Tag color={kind === 'official' ? 'gold' : 'cyan'}>{kind}</Tag>;
  }

  const Published = ({ published }: { published: boolean | undefined }) => {
    if (published === undefined) return null;

    return published
      ? <Tag icon={<CheckCircleOutlined />} color='success'>published</Tag>
      : <Tag icon={<StopOutlined />} color='default'>unpublished</Tag>;
  }

  const HostedState = ({ state }: { state: string | undefined }) => {
    if (!state) return null;

    switch (state) {
      case 'installing':
        return <Tag icon={<SyncOutlined spin />} color='processing'>installing</Tag>;
      case 'ready':
        return <Tag icon={<CheckCircleOutlined />} color='success'>ready</Tag>;
      case 'failed':
        return <Tag icon={<CloseCircleOutlined />} color='error'>failed</Tag>;
      case 'archived':
        return <Tag icon={<StopOutlined />} color='default'>archived</Tag>;
      default:
        return <Tag>{state}</Tag>;
    }
  }

  return (
    <div className='v-srv-meta'>
      <h6 className='v-content-label'>SERVER</h6>
      <hr className='v-divider' />

      <h6 className='v-content-label o-sm'>Kind</h6>
      <Kind kind={server?.kind} />

      <h6 className='v-content-label o-sm v-mt-15'>Status</h6>
      <Published published={server?.published} />

      <h6 className='v-content-label o-sm v-mt-15'>Connection</h6>
      <CopyValue value={server?.ip ? `${server.ip}:${server.port}` : ''} />

      <h6 className='v-content-label o-sm v-mt-15'>Version</h6>
      <div>{server?.version || '-'}</div>

      {hosted &&
        <>
          <h6 className='v-content-label v-mt-25'>HOSTED (CRAFTY-SERVERS)</h6>
          <hr className='v-divider' />

          <h6 className='v-content-label o-sm'>Provisioning</h6>
          <HostedState state={hosted.state} />

          <h6 className='v-content-label o-sm v-mt-15'>Plan</h6>
          <div>{hosted.server_plan_title || 'Custom'}</div>

          <h6 className='v-content-label o-sm v-mt-15'>Rent</h6>
          <div>{rentSummary(server)}</div>
          {hosted.paid_until && server?.kind !== 'official' &&
            <div className='v-srv-meta__exact'>
              {hosted.rent_coins} c_coins / month · auto-renew {hosted.auto_renew ? 'on' : 'off'}
            </div>
          }

          <h6 className='v-content-label o-sm v-mt-15'>Power</h6>
          <div>{powerLabel(hosted.config)}</div>

          <h6 className='v-content-label o-sm v-mt-15'>Players online</h6>
          <div title='As of the last sync — the Console tab has the live count'>
            {hosted.players_online ?? '-'}
          </div>

          <h6 className='v-content-label o-sm v-mt-15'>Crafty ID</h6>
          <CopyValue
            value={hosted.crafty_id}
            display={truncateMiddle(hosted.crafty_id || '', 12, 7)}
          />

          <h6 className='v-content-label o-sm v-mt-15'>Last sync</h6>
          {hosted.synced_at
            ? <>
                <div>{formattedDate(hosted.synced_at).fromNow}</div>
                <div className='v-srv-meta__exact'>{hosted.synced_at}</div>
              </>
            : <div>-</div>
          }

          {hosted.error &&
            <>
              <h6 className='v-content-label o-sm v-mt-15'>Error</h6>
              <div className='v-srv-meta__error'>{hosted.error}</div>
            </>
          }
        </>
      }
    </div>
  );
}

export default _ServerMeta;
